/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useEffect, useRef } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Button,
  Dialog, DialogContent, DialogTitle, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Zoom,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { ObjectSchema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useReactToPrint } from 'react-to-print';
import ComponentTextInput from '../../../../Common/ComponentTextinput/ComponentTextInput';
import ComponentSelectInput from '../../../../Common/ComponentSelectInput';
import { RootState } from '../../../../redux/rootState';
import { ComponentIssueInventoryInterface, IssueEquipmentFields } from '../../utils/types';
import { InventoryIssueEnum } from '../../utils/constants';
import { inventoryFormSchema, resetEquipmentIssueValues } from '../../utils/helper';
import { onIssueEquipment } from '../redux/inventorySlice';
import { CommonDateInput } from '../../../../Common/CommonDateInput';
import { Empty } from '../../../ScreenAddSmc/Utils/TypeSmc';
import NonJobEquipmentNeededPdfComponent from '../../SendEquipmentScreen/component/EquipmentNeededPdfComponent/NonJobEquipIssue';

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) => <Zoom ref={ref} {...props} />);
function ComponentEquipmentIssue(
  {
    show, setOpenFrom, products, callback,
  }: ComponentIssueInventoryInterface,
): JSX.Element {
  const componentRef = useRef(null);
  const { analystList } = useSelector((state: RootState) => state.bidsReducer);
  const { inventoryData } = useSelector((state: RootState) => state.inventoryReducer);

  const dispatch = useDispatch();
  const {
    handleSubmit, control, formState: { errors }, setValue, watch,
  } = useForm<IssueEquipmentFields>({
    resolver: yupResolver(inventoryFormSchema as unknown as ObjectSchema<IssueEquipmentFields>),
    defaultValues: {
      issuedDate: dayjs(),
    } as Empty,
  });

  const watchAnalyst = watch(InventoryIssueEnum.analyst);
  const watchInstructions = watch(InventoryIssueEnum.instructions);
  const shipDate = watch(InventoryIssueEnum.issuedDate) ? dayjs(watch(InventoryIssueEnum.issuedDate)).format('MM-DD-YYYY') : '';
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    removeAfterPrint: true,
    copyStyles: true,
  });
  const closeEquipmentIssueModal = () => {
    handlePrint();
    setOpenFrom();
    callback();
  };

  useEffect(() => {
    resetEquipmentIssueValues(setValue);
    const issuedDate = dayjs();
    setValue(InventoryIssueEnum.issuedDate, issuedDate);
  }, [products, show, inventoryData]);

  const onSubmit = (data: IssueEquipmentFields) => {
    const payload = {
      ProductNumber: products?.map((item) => item.id),
      AnalystId: data?.analyst,
      IssueDate: dayjs(data.issuedDate).format('DD-MM-YYYY'),
      Description: '',
    };
    dispatch(onIssueEquipment({ payload, callback: closeEquipmentIssueModal }));
  };

  return (
    <div>
      <Dialog
        maxWidth="sm"
        PaperProps={{
          className: 'paperStyle',
        }}
        open={show}
        transitionDuration={500}
        TransitionComponent={Transition}
        keepMounted
        onClose={setOpenFrom}
      >
        <DialogTitle className="modalHeaderTitle">
          Equipment Issue
          <IconButton
            aria-label="close"
            onClick={setOpenFrom}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 24px' }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              <Grid item md={6} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">
                    Analyst
                  </label>
                  <ComponentSelectInput
                    name={InventoryIssueEnum.analyst}
                    control={control}
                    required
                    errors={errors}
                    size="small"
                    entries={analystList}
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_Customer">
                    Date Issued
                  </label>
                  <CommonDateInput
                    control={control}
                    errors={errors}
                    name={InventoryIssueEnum.issuedDate}
                  />
                </div>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">Instructions for shipping list</label>
                  <ComponentTextInput
                    control={control}
                    className="nonCapitalizeField"
                    name={InventoryIssueEnum.instructions}
                    size="small"
                    id={InventoryIssueEnum.instructions}
                    errors={errors}
                    multiline
                    rowSize={4}
                  />
                </div>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <TableContainer component={Paper} elevation={4} style={{ maxHeight: '40vh' }}>
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableCell-root': {
                        fontSize: '12px',
                        padding: '6px 8px',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Product #</strong></TableCell>
                        <TableCell><strong>Serial #</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {products?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.id}</TableCell>
                          <TableCell>{item.serialNumber}</TableCell>
                          <TableCell>{item.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <div className="save_and_next_div">
                  <div>
                    <Button className="button_cancel" onClick={setOpenFrom}>Close</Button>
                  </div>
                  <div className="button_margin_left">
                    <Button disabled={!watchAnalyst} type="submit" className={!watchAnalyst ? 'disableBtn' : 'button_save_and_next'}>Issue</Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
      <div style={{ display: 'none' }}>
        <NonJobEquipmentNeededPdfComponent
          ref={componentRef}
          date={shipDate}
          analyst={watchAnalyst}
          products={products || []}
          instructions={watchInstructions}
        />
      </div>
    </div>
  );
}

export default ComponentEquipmentIssue;
